import { useEffect, useState } from "react";
import { client } from "../../lib/AmplifyClient";
import type { Schema } from "../../../amplify/data/resource";
import outputs from "../../../amplify_outputs.json";

// Strongly-typed Beta model from your Gen 2 schema
type BetaModel = Schema["Beta"]["type"];

const SEND_EMAIL_API_URL =
  ((outputs as any)?.custom?.sendEmailApiUrl as string) || "";

// Where accepted beta users should go to create an account / get started
// Use beta-only registration page, email passed via query string
const ACCOUNT_CREATE_BASE_URL = "https://accordiapro.com/beta-register";

export function BetaReviewDashboard() {
  const [items, setItems] = useState<BetaModel[]>([]);
  const [filteredItems, setFilteredItems] = useState<BetaModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "pending" | "accepted" | "declined"
  >("pending");
  const [processing, setProcessing] = useState<string | null>(null);

  useEffect(() => {
    loadSignups();
  }, [statusFilter]);

  useEffect(() => {
    filterItems();
  }, [items, searchTerm]);

  async function loadSignups() {
    setLoading(true);
    setError("");
    try {
      let res;

      if (statusFilter === "all") {
        res = await client.models.Beta.list({
          limit: 1000,
        });
      } else {
        res = await client.models.Beta.list({
          filter: {
            status: { eq: statusFilter },
          },
          limit: 1000,
        });
      }

      // Sort by createdAt (newest first)
      const sortedItems = [...res.data].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });

      setItems(sortedItems);
    } catch (err: any) {
      console.error("Error loading beta signups:", err);
      setError(err.message ?? "Failed to load applications.");
    } finally {
      setLoading(false);
    }
  }

  function filterItems() {
    if (!searchTerm.trim()) {
      setFilteredItems(items);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = items.filter(
      (item) =>
        item.firstName?.toLowerCase().includes(term) ||
        item.email?.toLowerCase().includes(term) ||
        item.useCase?.toLowerCase().includes(term)
    );
    setFilteredItems(filtered);
  }

  async function sendAcceptanceEmail(signup: BetaModel) {
    if (!SEND_EMAIL_API_URL) {
      console.warn(
        "SEND_EMAIL_API_URL is not configured; skipping acceptance email."
      );
      return;
    }

    if (!signup.email) {
      console.warn("Signup has no email; skipping acceptance email.");
      return;
    }

    const firstName = signup.firstName?.trim() || "there";
    const email = signup.email.trim();
    const situation = signup.useCase || "your situation";

    const subject = `You’re in: Accordia closed beta access confirmed`;

    // Build per-user account creation URL with their approved email
    const accountCreateUrl = `${ACCOUNT_CREATE_BASE_URL}?email=${encodeURIComponent(
      email
    )}`;

    const body = [
      `Hi ${firstName},`,
      "",
      "Good news — your request to join the Accordia closed beta has been accepted.",
      "",
      "What this gives you:",
      "• Access to an early version of Accordia for a 6–8 week period",
      "• Usually one guided conflict-resolution session per week with you and one other person",
      "• A structured, written workspace instead of unstructured arguments over text or email",
      "• A chance to shape how Accordia works with your feedback",
      "",
      "How Accordia works:",
      "1) Each of you completes a private intake so you can be fully heard.",
      "2) Accordia guides you through calmer, focused written conversations.",
      "3) You turn decisions into simple, plain-language written agreements you both can review later.",
      "",
      `You mentioned you might use Accordia for: ${situation}.`,
      "",
      "Next step: create your Accordia account and get set up for the beta:",
      accountCreateUrl,
      "",
      "Please do not share this link publicly. It is meant for you and the person you invite into your beta sessions.",
      "",
      "Reminder of what this beta is (and isn’t):",
      "• It is a time-limited early access program, not a permanent free tier.",
      "• It is not therapy, crisis support, or a law firm.",
      "• You can choose to share your written agreements with a mediator, attorney, or other professional if you wish.",
      "",
      "Thank you for helping us turn hard conversations into fair, documented agreements.",
      "",
      "Warm regards,",
      "The Accordia Team",
      "www.accordiapro.com",
      "—",
      'To stop receiving updates, reply “STOP.”',
    ].join("\n");

    try {
      const response = await fetch(SEND_EMAIL_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recipient: email,
          subject,
          body,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        console.error("Acceptance email API error:", response.status, text);
      }
    } catch (err) {
      console.error("Error calling SEND_EMAIL_API_URL for acceptance:", err);
    }
  }

  async function approve(id: string) {
    setProcessing(id);
    try {
      // Find the signup in local state so we can send email with its data
      const signup = items.find((i) => i.id === id);

      await client.models.Beta.update({
        id,
        status: "accepted",
      });

      // Fire-and-forget style acceptance email (logs errors but no alert)
      if (signup) {
        await sendAcceptanceEmail(signup);
      } else {
        console.warn(
          "Could not find signup in local state after approval; acceptance email not sent."
        );
      }

      await loadSignups();
    } catch (err) {
      console.error("Error approving beta signup:", err);
      alert("Could not approve. Check console for details.");
    } finally {
      setProcessing(null);
    }
  }

  async function reject(id: string) {
    setProcessing(id);
    try {
      await client.models.Beta.update({
        id,
        status: "declined",
      });
      await loadSignups();
    } catch (err) {
      console.error("Error rejecting beta signup:", err);
      alert("Could not reject. Check console for details.");
    } finally {
      setProcessing(null);
    }
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingBox}>Loading beta signups...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorBox}>
          <h2>⚠️ Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const displayItems = searchTerm ? filteredItems : items;
  const stats = {
    total: items.length,
    pending: items.filter((i) => i.status === "pending").length,
    accepted: items.filter((i) => i.status === "accepted").length,
    declined: items.filter((i) => i.status === "declined").length,
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Beta Review Dashboard</h1>
        <div style={styles.stats}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.total}</div>
            <div style={styles.statLabel}>Total</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: "#f59e0b" }}>
              {stats.pending}
            </div>
            <div style={styles.statLabel}>Pending</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: "#10b981" }}>
              {stats.accepted}
            </div>
            <div style={styles.statLabel}>Accepted</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: "#ef4444" }}>
              {stats.declined}
            </div>
            <div style={styles.statLabel}>Declined</div>
          </div>
        </div>
      </div>

      <div style={styles.controls}>
        <input
          type="text"
          placeholder="Search by name, email, or use case..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />

        <div style={styles.filterButtons}>
          {(["all", "pending", "accepted", "declined"] as const).map(
            (status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                style={{
                  ...styles.filterButton,
                  ...(statusFilter === status
                    ? styles.filterButtonActive
                    : {}),
                }}
              >
                {status.toUpperCase()}
              </button>
            )
          )}
        </div>
      </div>

      {displayItems.length === 0 ? (
        <div style={styles.emptyState}>
          <p>No {statusFilter} applications found.</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {displayItems.map((signup) => (
            <div key={signup.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div>
                  <h3 style={styles.cardTitle}>{signup.firstName}</h3>
                  <div style={styles.cardEmail}>{signup.email}</div>
                </div>
                <span
                  style={{
                    ...styles.badge,
                    ...getBadgeStyle(signup.status || "pending"),
                  }}
                >
                  {(signup.status || "pending").toUpperCase()}
                </span>
              </div>

              <div style={styles.cardBody}>
                <div style={styles.field}>
                  <strong>Use Case:</strong>
                  <p style={styles.useCase}>{signup.useCase || "—"}</p>
                </div>

                {signup.source && (
                  <div style={styles.field}>
                    <strong>Source:</strong> {signup.source}
                  </div>
                )}

                <div style={styles.field}>
                  <strong>Submitted:</strong>{" "}
                  {signup.createdAt
                    ? new Date(signup.createdAt).toLocaleString()
                    : "—"}
                </div>
              </div>

              {signup.status === "pending" && (
                <div style={styles.cardActions}>
                  <button
                    onClick={() => approve(signup.id)}
                    disabled={processing === signup.id}
                    style={{
                      ...styles.button,
                      ...styles.approveButton,
                      ...(processing === signup.id
                        ? styles.buttonDisabled
                        : {}),
                    }}
                  >
                    {processing === signup.id
                      ? "Processing..."
                      : "✓ Approve & Email"}
                  </button>
                  <button
                    onClick={() => reject(signup.id)}
                    disabled={processing === signup.id}
                    style={{
                      ...styles.button,
                      ...styles.rejectButton,
                      ...(processing === signup.id
                        ? styles.buttonDisabled
                        : {}),
                    }}
                  >
                    {processing === signup.id ? "Processing..." : "✗ Reject"}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function getBadgeStyle(status: string) {
  switch (status) {
    case "pending":
      return { backgroundColor: "#fef3c7", color: "#92400e" };
    case "accepted":
      return { backgroundColor: "#d1fae5", color: "#065f46" };
    case "declined":
      return { backgroundColor: "#fee2e2", color: "#991b1b" };
    case "invited":
      return { backgroundColor: "#dbeafe", color: "#1e40af" };
    case "active":
      return { backgroundColor: "#d1fae5", color: "#065f46" };
    case "closed":
      return { backgroundColor: "#f3f4f6", color: "#1f2937" };
    default:
      return { backgroundColor: "#f3f4f6", color: "#1f2937" };
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: "24px",
    maxWidth: "1400px",
    margin: "0 auto",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: "32px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    marginBottom: "24px",
    color: "#111827",
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  statCard: {
    padding: "20px",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    textAlign: "center",
  },
  statValue: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#1f2937",
    marginBottom: "4px",
  },
  statLabel: {
    fontSize: "14px",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  controls: {
    marginBottom: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  searchInput: {
    padding: "12px 16px",
    fontSize: "15px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    width: "100%",
    outline: "none",
  },
  filterButtons: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  filterButton: {
    padding: "8px 16px",
    fontSize: "14px",
    fontWeight: "500",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    backgroundColor: "#ffffff",
    color: "#374151",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  filterButtonActive: {
    backgroundColor: "#1a73e8",
    color: "#ffffff",
    borderColor: "#1a73e8",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
    gap: "20px",
  },
  card: {
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "20px",
    transition: "box-shadow 0.2s",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "16px",
    paddingBottom: "16px",
    borderBottom: "1px solid #f3f4f6",
  },
  cardTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#111827",
    margin: "0 0 4px 0",
  },
  cardEmail: {
    fontSize: "14px",
    color: "#6b7280",
  },
  badge: {
    padding: "4px 12px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  cardBody: {
    marginBottom: "16px",
  },
  field: {
    marginBottom: "12px",
    fontSize: "14px",
    color: "#374151",
  },
  useCase: {
    marginTop: "4px",
    padding: "12px",
    backgroundColor: "#f9fafb",
    borderRadius: "6px",
    fontSize: "13px",
    lineHeight: "1.6",
    color: "#1f2937",
  },
  cardActions: {
    display: "flex",
    gap: "12px",
  },
  button: {
    flex: 1,
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  approveButton: {
    backgroundColor: "#10b981",
    color: "#ffffff",
  },
  rejectButton: {
    backgroundColor: "#ef4444",
    color: "#ffffff",
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  emptyState: {
    textAlign: "center",
    padding: "48px",
    color: "#6b7280",
  },
  loadingBox: {
    textAlign: "center",
    padding: "48px",
    fontSize: "16px",
    color: "#6b7280",
  },
  errorBox: {
    padding: "24px",
    backgroundColor: "#fee2e2",
    border: "1px solid #fecaca",
    borderRadius: "8px",
    color: "#991b1b",
  },
};
